/**
 * Firebase app initialization — single shared instance for the whole app.
 * Import the exported services (auth, db, storage) rather than re-initializing.
 *
 * Values are read from config/env, which reads REACT_APP_FIREBASE_* vars.
 * Copy `.env.example` to `.env.local` and fill in your project's keys — until
 * then Firebase calls will fail, which is expected.
 */
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  connectAuthEmulator,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

import { env } from 'config';

const firebaseApp = initializeApp(env.firebase);

/**
 * App Check — attests that a request came from THIS app, not from a script
 * holding a valid user token. It matters most for `accountLogin`, the one
 * public callable, whose reset action can otherwise be looped to exhaust the
 * shared Gmail send quota and silence every notification email.
 *
 * Initialised only when a site key is configured, so a build without one behaves
 * exactly as before rather than attaching tokens the backend can't verify.
 * Enforcement is a separate, later switch on the backend (ENFORCE_APP_CHECK in
 * functions/src/config.js) — ship tokens first, watch the metrics, then enforce.
 */
if (env.appCheckSiteKey) {
  try {
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaEnterpriseProvider(env.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // A bad key must not take the whole app down at import time — App Check is
    // a hardening layer, and the server-side rate limits stand on their own.
    // eslint-disable-next-line no-console
    console.warn('[firebase] App Check not initialised:', error?.message);
  }
}

export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);
// Region must match REGION in functions/src/config.js — callables are
// region-pinned and a mismatch fails with internal/CORS errors.
export const functions = getFunctions(firebaseApp, 'us-central1');

// Local development against the Firebase emulator suite (firebase.json ports).
// Opt-in via REACT_APP_USE_EMULATORS=true so production builds never connect.
// The REACT_APP_EMULATOR_*_PORT overrides exist for a machine already running
// another project's emulators on the defaults: the auth emulator serves a
// single default project, so two projects cannot share its port.
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  const authPort = Number(process.env.REACT_APP_EMULATOR_AUTH_PORT) || 9099;
  const firestorePort = Number(process.env.REACT_APP_EMULATOR_FIRESTORE_PORT) || 8080;
  const functionsPort = Number(process.env.REACT_APP_EMULATOR_FUNCTIONS_PORT) || 5001;
  connectAuthEmulator(auth, `http://localhost:${authPort}`, { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', firestorePort);
  connectFunctionsEmulator(functions, 'localhost', functionsPort);
}

/**
 * Persist the session in the browser (IndexedDB) so a refresh or a return
 * visit keeps the user signed in. This is Firebase's default, but we set it
 * explicitly so the intent — and the "persist sessions" requirement — is
 * documented and guaranteed.
 */
setPersistence(auth, browserLocalPersistence).catch((error) => {
  // Non-fatal: some privacy modes block storage. Auth still works in-memory.
  // eslint-disable-next-line no-console
  console.warn('[firebase] Could not set local auth persistence:', error?.message);
});

export default firebaseApp;
